import { EmptyState } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { useMarkNotificationsRead, useNotifications } from '../lib/ordering.js';

const ICONS: Record<string, string> = {
  cutoff: '⏳',
  confirmed: '✓',
  menu: '🗓',
  notice: '📍',
};

/**
 * Alerts.
 *
 * Unread sits on amber rather than carrying a dot: the one that matters is
 * almost always the cutoff warning, and a colour says that faster than a badge.
 * Everything else is a white card with an icon tile.
 */
export default function AlertsPage() {
  const { items, unread } = useNotifications();
  const markAllRead = useMarkNotificationsRead();
  const { t } = useT();

  return (
    <>
      <div className="flex items-center px-[22px] pt-[calc(22px+env(safe-area-inset-top))] md:px-2 md:pt-7">
        <h2 className="text-[25px] font-extrabold md:text-[30px]">{t('Alerts')}</h2>
        <button
          type="button"
          onClick={() => markAllRead()}
          disabled={unread === 0}
          className="ml-auto text-[13px] md:text-[14.5px] font-bold text-brand disabled:text-subtle"
        >
          {t('Mark all read')}
        </button>
      </div>

      <div className="flex flex-col gap-[11px] px-[22px] pt-5 md:grid md:grid-cols-2 md:gap-3.5 md:px-2 md:pt-6">
        {items.length === 0 && (
          <div className="md:col-span-2">
            <EmptyState
              title={t('Nothing yet')}
              body={t('Cutoff reminders and menu updates arrive here.')}
            />
          </div>
        )}

        {items.map((n) => (
          <div
            key={n.id}
            className={`flex gap-[13px] rounded-tile px-[17px] py-4 ${
              n.readAt === null ? 'bg-warn-soft' : 'bg-white'
            }`}
          >
            <span
              aria-hidden
              className="flex size-10 shrink-0 items-center justify-center rounded-[14px] bg-white text-[17px] md:text-[18.5px]"
            >
              {ICONS[n.kind] ?? '•'}
            </span>
            <div className="min-w-0">
              <b className="mb-[3px] block text-[14.5px] md:text-[15.5px] text-pretty">{n.title}</b>
              <span className="text-[13px] md:text-[14.5px] leading-[1.5] text-muted">{n.body}</span>
              <span className="mt-[5px] block text-[13px] md:text-[14.5px] font-semibold text-subtle">
                {new Date(n.createdAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </span>
            </div>
          </div>
        ))}

        <p className="px-1 py-0.5 text-[13px] text-faint md:col-span-2 md:text-[14.5px]">
          {t('Email is on by default. Push and SMS come in a later version.')}
        </p>
      </div>
    </>
  );
}
